import { useMemo } from "react";
import { navigateTo } from "../../router";
import { ReplayStepper } from "./components/ReplayStepper";
import type { YinshReplayEventType } from "./event-adapter";
import {
  buildYinshReplaySnapshots,
  type YinshReplaySnapshot,
} from "./replay-runner";

/**
 * Renders a YINSH replay from a resolved share's raw event log. Used by the
 * game-agnostic ReplayRoomScreen, dispatched on gameType. Mirrors the
 * fixture-driven ReplayScreen but takes events directly instead of a scenario.
 */
interface YinshReplayRoomViewProps {
  readonly events: readonly unknown[];
}

interface BuildResult {
  readonly snapshots: readonly YinshReplaySnapshot[];
  readonly error: string | null;
}

export function YinshReplayRoomView({ events }: YinshReplayRoomViewProps) {
  const { snapshots, error } = useMemo<BuildResult>(() => {
    try {
      const typed = events.map((event) => {
        const ev = event as {
          sequenceNumber: number;
          eventType: string;
          userId: string;
          payload?: Record<string, unknown>;
        };
        return {
          sequenceNumber: ev.sequenceNumber,
          eventType: ev.eventType as YinshReplayEventType,
          userId: ev.userId,
          payload: ev.payload ?? {},
        };
      });
      return { snapshots: buildYinshReplaySnapshots(typed), error: null };
    } catch (cause) {
      return {
        snapshots: [],
        error:
          cause instanceof Error
            ? cause.message
            : "리플레이를 재생할 수 없습니다",
      };
    }
  }, [events]);

  if (error || snapshots.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 재생할 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">
            {error ?? "이벤트가 비어 있습니다"}
          </p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/replay/lobby")}
          >
            돌아가기
          </button>
        </section>
      </div>
    );
  }

  const last = snapshots[snapshots.length - 1]?.state;

  return (
    <ReplayStepper
      snapshots={snapshots}
      whiteName={last?.players[0]?.name ?? "WHITE"}
      blackName={last?.players[1]?.name ?? "BLACK"}
      onExit={() => navigateTo("/replay/lobby")}
    />
  );
}
